import { useEffect, useRef, useState } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: string;
  className?: string;
}

export default function Select({
  value,
  options,
  onChange,
  placeholder = 'اختر',
  icon,
  className = '',
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div ref={ref} className={['relative', className].join(' ')}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={[
          'flex h-11 w-full items-center gap-2 rounded-field border bg-white px-3 text-sm transition-colors',
          open
            ? 'border-secondary-400 ring-2 ring-secondary-100'
            : 'border-background-300 hover:border-background-400',
        ].join(' ')}
      >
        {icon && <i className={`${icon} text-base text-foreground-400`} aria-hidden="true"></i>}
        <span
          className={[
            'flex-1 truncate text-right',
            selected ? 'text-foreground-900' : 'text-foreground-400',
          ].join(' ')}
        >
          {selected ? selected.label : placeholder}
        </span>
        <i
          className={[
            'ri-arrow-down-s-line text-base text-foreground-400 transition-transform duration-200',
            open ? 'rotate-180' : '',
          ].join(' ')}
          aria-hidden="true"
        ></i>
      </button>

      {open && (
        <div className="dc-fade-up absolute inset-x-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-card border border-background-200 bg-white p-1 shadow-soft">
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={[
                  'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-right text-[13px] transition-colors',
                  isSelected
                    ? 'bg-secondary-50 text-secondary-700'
                    : 'text-foreground-700 hover:bg-background-50',
                ].join(' ')}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <i className="ri-check-line text-base" aria-hidden="true"></i>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}